import Image from 'next/image';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { StepsSectionDTO } from '@lib/strapi/dtos/components/sections/stepsSection.dto';
import { Prose, SectionHeading } from '../shared-ui';

/**
 * The assessment journey (D8). Numbered because the content genuinely is a
 * sequence — the number markers carry information here, they are not
 * decoration. Rendered as a VERTICAL timeline (owner, 2026-09-08): a rail
 * connects the numbered stops top-to-bottom, which reads as a journey — the
 * old three-across cards read as alternatives.
 */
const StepsSectionImplementation = ({ section }: ComponentProps<StepsSectionDTO>) => {
  if (!section?.steps?.length) return null;
  const last = section.steps.length - 1;
  // With no section heading there is no h2 between the page h1 and the step
  // titles — the h3s then violate heading order (Lighthouse heading-order,
  // 2026-09-14). The step titles take the level that follows what exists.
  const StepTitle = section.heading ? 'h3' : 'h2';

  return (
    <section className="my-14 md:my-20">
      <SectionHeading heading={section.heading} intro={section.intro} />
      {/* Designer pass 2026-09-14 (istoriko.psd): circular photo LEFT of each
          stop, the numbered bubble ON the rail between photo and text. The
          photo column hides on small screens — the rail + text is the journey,
          the photo is garnish. */}
      <ol className="mx-auto flex max-w-3xl flex-col">
        {section.steps.map((step, i) => (
          <li key={i} className="flex gap-5 sm:gap-6">
            {step.image ? (
              <div className={`hidden flex-none sm:block ${i < last ? 'pb-10' : ''}`}>
                <div className="relative aspect-square w-44 overflow-hidden rounded-full bg-violet-050 md:w-48">
                  <Image src={step.image.url} alt="" fill sizes="12rem" className="portrait-photo" />
                </div>
              </div>
            ) : null}
            {/* the bubble OVERLAPS the photo's right edge (istoriko.psd) —
                negative margin + white ring so the join reads crisp */}
            <div className={`relative z-10 flex flex-col items-center ${step.image ? 'sm:-ml-10' : ''}`} aria-hidden="true">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-full bg-brand-script text-base font-extrabold text-white shadow-[0_4px_12px_rgba(151,113,255,0.35)]">
                {i + 1}
              </span>
              {i < last ? <span className="my-2 w-[3px] flex-1 rounded-full bg-violet-200" /> : null}
            </div>
            <div className={`min-w-0 pt-1.5 ${i < last ? 'pb-10' : ''}`}>
              <StepTitle className="text-lg font-extrabold text-ink">{step.heading}</StepTitle>
              <Prose content={step.description} className="mt-2" />
              {step.image ? (
                <div className="blob-2 relative mt-4 aspect-[4/3] max-w-xs overflow-hidden bg-violet-050 sm:hidden">
                  <Image src={step.image.url} alt="" fill sizes="20rem" className="portrait-photo" />
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
};

export default StepsSectionImplementation;
